// src/pages/Pricing.jsx
// Interactive volume-based pricing page with seat calculator

import React, { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Check, Shield, Zap, Users, BarChart3, MessageSquare,
  Calendar, FileText, Lock, ArrowRight, Star, Building2,
  Sparkles, Phone, ChevronDown, TrendingUp, Bell, Search,
} from "lucide-react";

const brand = {
  navy: "#172A3A",
  gold: "#FFB800",
};

// Exact pricing tiers — must stay in sync with backend/utils/pricing.js
const PRICING_TIERS = [
  { min: 1,   max: 1,   perAgent: 129, label: 'Solo' },
  { min: 2,   max: 2,   perAgent: 119, label: 'Small Team' },
  { min: 3,   max: 3,   perAgent: 115, label: 'Small Team' },
  { min: 4,   max: 4,   perAgent: 109, label: 'Small Team' },
  { min: 5,   max: 5,   perAgent: 105, label: 'Small Team' },
  { min: 6,   max: 10,  perAgent: 99,  label: 'Team' },
  { min: 11,  max: 15,  perAgent: 95,  label: 'Team' },
  { min: 16,  max: 20,  perAgent: 92,  label: 'Team' },
  { min: 21,  max: 30,  perAgent: 89,  label: 'Agency' },
  { min: 31,  max: 40,  perAgent: 85,  label: 'Agency' },
  { min: 41,  max: 50,  perAgent: 82,  label: 'Agency' },
  { min: 51,  max: 75,  perAgent: 79,  label: 'Enterprise' },
  { min: 76,  max: 100, perAgent: 75,  label: 'Enterprise' },
];

function getPricing(seats) {
  if (seats > 100) return { requiresContactSales: true };
  const tier = PRICING_TIERS.find(t => seats >= t.min && seats <= t.max);
  if (!tier) return { requiresContactSales: true };
  const savings = Math.round(((129 - tier.perAgent) / 129) * 100);
  return {
    requiresContactSales: false,
    perAgent: tier.perAgent,
    total: tier.perAgent * seats,
    savings,
  };
}

function Feature({ text, icon: Icon }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 bg-blue-500/20">
        <Check size={11} className="text-blue-400" />
      </div>
      <span className="text-slate-300 text-sm leading-relaxed">{text}</span>
    </div>
  );
}

function FaqItem({ q, a }) {
  return (
    <details className="group bg-slate-800/30 border border-slate-700/50 rounded-xl">
      <summary className="flex items-center justify-between p-5 cursor-pointer text-white font-medium select-none list-none">
        <span className="pr-4">{q}</span>
        <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0 transition-transform group-open:rotate-180" />
      </summary>
      <div className="px-5 pb-5 text-slate-400 text-sm leading-relaxed">
        {a}
      </div>
    </details>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const [seats, setSeats] = useState(1);
  const [inputVal, setInputVal] = useState('1');

  const pricing = useMemo(() => getPricing(seats), [seats]);

  function handleSliderChange(e) {
    const val = parseInt(e.target.value);
    setSeats(val);
    setInputVal(String(val));
  }

  function handleInputChange(e) {
    const raw = e.target.value.replace(/\D/g, '');
    setInputVal(raw);
    const n = parseInt(raw);
    if (n >= 1) setSeats(Math.min(n, 150));
  }

  function handleInputBlur() {
    const n = parseInt(inputVal);
    if (!n || n < 1) {
      setSeats(1);
      setInputVal('1');
    } else {
      const clamped = Math.min(n, 150);
      setSeats(clamped);
      setInputVal(String(clamped));
    }
  }

  const features = [
    "Unlimited Medicare client profiles",
    "AI-powered churn-risk scoring & early warnings",
    "CMS Blue Button health data integration",
    "Automated SMS & email retention outreach",
    "AEP & OEP campaign management tools",
    "Smart PDF prescription parser",
    "Agent prospecting pipeline",
    "Carrier plan comparison & analytics",
    "Real-time at-risk client alerts",
    "HIPAA-compliant, AES-256 encrypted storage",
    "Business Associate Agreement (BAA) included",
    "Priority onboarding & support",
  ];

  const testimonials = [
    {
      quote: "Pro IRP helped me save 23 clients during AEP that I would have lost. The ROI is incredible.",
      author: "Michael R.",
      role: "Independent Agent, FL",
      result: "$27,600/yr retained",
    },
    {
      quote: "The Blue Button integration alone is worth 10x the price. I know exactly when my clients' health changes.",
      author: "Sarah T.",
      role: "Medicare Specialist, TX",
      result: "45 at-risk alerts/month",
    },
    {
      quote: "Finally, a CRM built for Medicare retention. The churn predictions are scary accurate.",
      author: "David L.",
      role: "Agency Owner, AZ",
      result: "31% churn reduction",
    },
  ];

  const sliderPercent = ((Math.min(seats, 100) - 1) / 99) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">

      {/* Sticky header */}
      <header className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.png" alt="Pro IRP" className="w-9 h-9 rounded-full bg-white shadow" />
              <span className="font-extrabold text-white text-xl">
                Pro <span style={{ color: brand.gold }}>IRP</span>
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-slate-300 hover:text-white transition font-medium text-sm hidden sm:block">
                Log in
              </Link>
              <Link
                to="/signup"
                className="px-5 py-2 rounded-lg font-semibold text-sm transition hover:opacity-90"
                style={{ background: brand.gold, color: brand.navy }}
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-16 pb-6 px-4 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400 text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Trusted by 500+ Medicare agents & agencies
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight">
            Transparent pricing.
            <br />
            <span style={{ color: brand.gold }}>Volume discounts that scale.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-400 max-w-xl mx-auto leading-relaxed">
            Every seat. Every feature. One straightforward rate based on your team size.
            No hidden fees. No feature tiers. No surprises at checkout.
          </p>
        </div>
      </section>

      {/* ─── Interactive Calculator ─── */}
      <section className="py-10 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-slate-800/60 backdrop-blur border border-slate-700/60 rounded-3xl p-8 shadow-2xl shadow-black/20">

            <div className="text-center mb-8">
              <h2 className="text-base font-semibold text-slate-300 mb-1">
                How many agents on your team?
              </h2>
              <p className="text-xs text-slate-500">
                Drag the slider or type a number — see your exact price instantly
              </p>
            </div>

            {/* Seat input */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <Users className="w-5 h-5 text-slate-400 flex-shrink-0" />
              <span className="text-slate-400 text-sm font-medium">Agents:</span>
              <input
                type="text"
                inputMode="numeric"
                value={inputVal}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className="w-20 text-center text-2xl font-bold text-white bg-slate-700/70 border border-slate-600 rounded-xl py-2 px-3 focus:outline-none focus:border-blue-500 transition"
                maxLength={3}
              />
              <span className="text-slate-500 text-sm">
                seat{seats !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Slider */}
            <div className="mb-8 px-1">
              <input
                type="range"
                min={1}
                max={100}
                value={Math.min(seats, 100)}
                onChange={handleSliderChange}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, ${brand.gold} 0%, ${brand.gold} ${sliderPercent}%, #334155 ${sliderPercent}%, #334155 100%)`,
                }}
              />
              <div className="flex justify-between text-xs text-slate-600 mt-2 px-0.5">
                <span>1</span>
                <span>25</span>
                <span>50</span>
                <span>75</span>
                <span>100+</span>
              </div>
            </div>

            {/* Price result */}
            {pricing.requiresContactSales ? (
              <div className="text-center bg-slate-900/50 rounded-2xl p-6">
                <div className="text-3xl font-extrabold text-white mb-1">Let's talk</div>
                <p className="text-slate-400 text-sm mb-5 leading-relaxed">
                  For teams of 101+ agents we offer custom enterprise pricing,
                  SSO/SAML, dedicated success management, and SLA guarantees.
                </p>
                <a
                  href="mailto:sales@proirp.com"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-base transition hover:opacity-90"
                  style={{ background: brand.gold, color: brand.navy }}
                >
                  <Phone className="w-4 h-4" />
                  Contact Sales
                </a>
              </div>
            ) : (
              <div className="bg-slate-900/50 rounded-2xl p-6 text-center">
                {seats > 1 && (
                  <p className="text-slate-500 text-sm mb-1">
                    {seats} agents × ${pricing.perAgent}/agent/month
                  </p>
                )}
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-6xl font-extrabold text-white">
                    ${pricing.total.toLocaleString()}
                  </span>
                  <span className="text-slate-400 text-lg">/month</span>
                </div>

                <div className="flex items-center justify-center gap-3 mt-2 min-h-[24px]">
                  {seats === 1 ? (
                    <span className="text-sm text-slate-500">$129/agent/month</span>
                  ) : (
                    <>
                      <span className="text-sm text-slate-500">${pricing.perAgent}/agent</span>
                      {pricing.savings > 0 && (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                          Save {pricing.savings}% vs solo rate
                        </span>
                      )}
                    </>
                  )}
                </div>

                <p className="text-xs text-slate-600 mt-3">
                  All features included · No setup fee · Cancel anytime
                </p>

                <button
                  onClick={() => navigate('/signup')}
                  className="mt-5 w-full py-3.5 rounded-xl font-bold text-base transition hover:opacity-90 flex items-center justify-center gap-2"
                  style={{ background: brand.gold, color: brand.navy }}
                >
                  Get Started — {seats} Agent{seats !== 1 ? 's' : ''}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Value proof */}
      <section className="py-4 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-blue-500/8 border border-blue-500/20 rounded-2xl px-6 py-5 text-center">
            <p className="text-blue-300 text-sm leading-relaxed">
              The average Medicare Advantage commission is{" "}
              <strong className="text-white">$600–$900 per client per year</strong>.
              At $129/month, retaining just{" "}
              <strong className="text-white">2 extra clients</strong> pays for an entire year of Pro IRP.
            </p>
          </div>
        </div>
      </section>

      {/* ─── Volume tiers table ─── */}
      <section className="py-14 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl font-bold text-white text-center mb-2">
            Full pricing breakdown
          </h2>
          <p className="text-center text-sm text-slate-500 mb-7">
            Click any row to preview that tier in the calculator above
          </p>

          <div className="overflow-hidden rounded-2xl border border-slate-700/50">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/80 border-b border-slate-700/50">
                  <th className="text-left px-5 py-3 text-slate-400 font-medium">Team size</th>
                  <th className="text-right px-5 py-3 text-slate-400 font-medium">Per agent / mo</th>
                  <th className="text-right px-5 py-3 text-slate-400 font-medium hidden sm:table-cell">Example total</th>
                  <th className="text-right px-5 py-3 text-slate-400 font-medium hidden sm:table-cell">vs solo</th>
                </tr>
              </thead>
              <tbody>
                {PRICING_TIERS.map((tier, i) => {
                  const label = tier.min === tier.max
                    ? `${tier.min} agent${tier.min !== 1 ? 's' : ''}`
                    : `${tier.min}–${tier.max} agents`;
                  const savings = Math.round(((129 - tier.perAgent) / 129) * 100);
                  const isActive = !pricing.requiresContactSales && seats >= tier.min && seats <= tier.max;

                  return (
                    <tr
                      key={i}
                      onClick={() => { setSeats(tier.min); setInputVal(String(tier.min)); }}
                      className={`border-b border-slate-700/25 cursor-pointer transition-colors ${
                        isActive
                          ? 'bg-blue-500/10 border-l-2 border-l-blue-500'
                          : 'hover:bg-slate-800/40'
                      }`}
                    >
                      <td className={`px-5 py-3.5 font-medium ${isActive ? 'text-white' : 'text-slate-300'}`}>
                        {label}
                        {isActive && (
                          <span className="ml-2 text-xs text-blue-400 font-normal">← your tier</span>
                        )}
                      </td>
                      <td className={`px-5 py-3.5 text-right font-bold tabular-nums ${isActive ? 'text-white' : 'text-slate-300'}`}>
                        ${tier.perAgent}
                      </td>
                      <td className="px-5 py-3.5 text-right text-slate-500 tabular-nums hidden sm:table-cell">
                        ${(tier.perAgent * tier.min).toLocaleString()}
                      </td>
                      <td className="px-5 py-3.5 text-right hidden sm:table-cell">
                        {savings > 0
                          ? <span className="text-emerald-400 text-xs font-medium">−{savings}%</span>
                          : <span className="text-slate-600 text-xs">—</span>
                        }
                      </td>
                    </tr>
                  );
                })}
                {/* Enterprise row */}
                <tr className="bg-gradient-to-r from-blue-500/5 to-violet-500/5">
                  <td className="px-5 py-3.5 font-medium text-slate-300">101+ agents</td>
                  <td className="px-5 py-3.5 text-right font-bold text-slate-300" colSpan={2}>
                    <a
                      href="mailto:sales@proirp.com"
                      className="text-blue-400 hover:underline"
                      onClick={e => e.stopPropagation()}
                    >
                      Contact Sales →
                    </a>
                  </td>
                  <td className="px-5 py-3.5 text-right hidden sm:table-cell">
                    <span className="text-emerald-400 text-xs font-medium">Best Rate</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-center text-xs text-slate-600 mt-3">
            All seats priced at one uniform rate — no blended billing
          </p>
        </div>
      </section>

      {/* ─── Features ─── */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl font-bold text-white text-center mb-2">
            Everything included — every plan
          </h2>
          <p className="text-center text-sm text-slate-500 mb-8">
            No feature gating. Solo agents and enterprise agencies get the same platform.
          </p>
          <div className="grid sm:grid-cols-2 gap-3.5">
            {features.map((f, i) => (
              <Feature key={i} text={f} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── Testimonials ─── */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-bold text-white text-center mb-8">
            Agents who protect their book with Pro IRP
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-slate-800/30 border border-slate-700/40 rounded-2xl p-5">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-300 text-sm italic leading-relaxed mb-4">
                  "{t.quote}"
                </p>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="font-semibold text-white text-sm">{t.author}</p>
                    <p className="text-xs text-slate-500">{t.role}</p>
                  </div>
                  <span className="text-xs font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full">
                    {t.result}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Enterprise CTA ─── */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-blue-600/15 to-violet-600/15 border border-blue-500/25 rounded-3xl p-8 text-center">
            <Building2 className="w-10 h-10 text-blue-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">FMO or large agency?</h2>
            <p className="text-slate-400 text-sm mb-1 leading-relaxed">
              101+ agents — we offer custom pricing, SSO/SAML, dedicated success managers, and SLA guarantees.
            </p>
            <p className="text-slate-500 text-sm mb-6">
              Contact sales for a custom quote tailored to your organization.
            </p>
            <a
              href="mailto:sales@proirp.com"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold border border-white/20 hover:bg-white/10 transition text-sm"
            >
              Contact Sales
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl font-bold text-white text-center mb-8">
            Frequently asked questions
          </h2>
          <div className="space-y-3">
            <FaqItem
              q="How does per-seat pricing work?"
              a="You pay one uniform rate per agent seat based on your total seat count. For example, a 10-agent team pays $99/agent × 10 = $990/month. All seats are priced at the same rate — no blended billing, no complexity. Your per-agent rate may drop when you add seats into a new volume tier."
            />
            <FaqItem
              q="What exactly counts as an agent seat?"
              a="One seat = one licensed Medicare agent with their own login. Agency administrators, assistants, or read-only users don't consume agent seats. If you're unsure how many seats you need, reach out and we'll help you figure it out."
            />
            <FaqItem
              q="Is the price I see in the calculator exactly what I'll be charged?"
              a="Yes — always. The price shown in the calculator appears exactly on your invoice and Stripe checkout. No setup fees, no onboarding fees, no surprise line items. What you see is what you pay."
            />
            <FaqItem
              q="Can I add or remove seats mid-cycle?"
              a="Yes. Adding seats is prorated to the day. Removing seats takes effect at the next billing cycle. Your per-agent rate recalculates any time your total seat count changes tiers."
            />
            <FaqItem
              q="Do you offer annual billing?"
              a="Annual plans with additional savings are in the works. If you'd like to lock in annual pricing now, contact us — we'll work something out."
            />
            <FaqItem
              q="Is Pro IRP HIPAA compliant?"
              a="Yes. All client data is encrypted at rest (AES-256) and in transit (TLS 1.2+). We run on AWS infrastructure with SOC 2 controls. A Business Associate Agreement (BAA) is included for all paid accounts."
            />
            <FaqItem
              q="What payment methods do you accept?"
              a="All major credit and debit cards (Visa, Mastercard, Amex, Discover) via Stripe. ACH bank transfers are available for annual enterprise plans."
            />
            <FaqItem
              q="Can I cancel anytime?"
              a="Yes. Cancel with one click from your account settings. No cancellation fees, no contracts, no phone calls required. Your data remains available for export for 30 days after cancellation."
            />
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-3">
            Stop losing clients to churn.
          </h2>
          <p className="text-slate-400 mb-8">
            Join 500+ Medicare agents who trust Pro IRP to protect their book of business.
          </p>
          <button
            onClick={() => navigate('/signup')}
            className="px-8 py-4 rounded-xl font-bold text-lg transition hover:opacity-90"
            style={{ background: brand.gold, color: brand.navy }}
          >
            Get Started Today
          </button>
          <p className="text-sm text-slate-600 mt-4">No credit card required to start</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Pro IRP" className="w-8 h-8 rounded-full bg-white" />
            <span className="text-slate-400 text-sm">
              &copy; {new Date().getFullYear()} Pro IRP. All rights reserved.
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <Link to="/terms" className="hover:text-white transition">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition">Privacy</Link>
            <a href="mailto:support@proirp.com" className="hover:text-white transition">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
